/**
 * GameScene — the only Phaser scene in this game. Owns the workspace UI,
 * port/pipe/organ lifecycle, and all input wiring.
 *
 * INVARIANTS (don't break these when refactoring):
 *
 * • Depth ordering (global, every scene object respects it):
 *     workspace shell  -10
 *     grid dots         -5
 *     pipe segments      5
 *     in-pipe molecules  8
 *     organ sprites     10
 *     port containers   20
 *     burst-X / spurts  28–30
 *     NEXT STEP button  60
 *     drag ghost        80
 *
 * • Grid is the source of truth: pipe segments are stored as `{col, row, shape}`,
 *   organs as a top-left `{col, row}`. World coords are derived via `gridToWorld()`
 *   only at render time. Any new mechanic that moves things should operate in
 *   grid cells, NOT world coordinates.
 *
 * • Pipe direction is always spout → receptacle. If a drag starts from the
 *   receptacle end, PipeBuilder calls `Pipe.reverseDirection` on connect.
 *
 * • Direct organ-to-organ link forms iff: (1) both ports are on placed organs,
 *   (2) they share the same `connectionCell`, (3) `moleculeMixEqual(carries)`.
 *   Both port shapes should be `castellated` for the joint to render correctly,
 *   though the linkage logic doesn't enforce it.
 *
 * • Two organs may overlap by exactly a 1-cell strip when both have castellated
 *   ports — that strip is the joint row/col. Any other overlap is rejected.
 *
 * • Level lifecycle: `loadLevel(i)` creates ports + populates the organ pool;
 *   `unloadLevel()` destroys everything (ports, pipes, organs, FX); `goToLevel(i)`
 *   is unload + load + saveLevel. localStorage holds the last index.
 */

import Phaser from 'phaser';
import { Organ } from '../entities/Organ';
import { Pipe } from '../entities/Pipe';
import { Port } from '../entities/Port';
import { Connector } from '../systems/Connector';
import { PipeBuilder } from '../systems/PipeBuilder';
import { Clipboard } from '../ui/Clipboard';
import { CompletionOverlay } from '../ui/CompletionOverlay';
import { drawLegend } from '../ui/Legend';
import { OrganPool } from '../ui/OrganPool';
import { TopBar } from '../ui/TopBar';
import { drawWorkspaceShell } from '../ui/WorkspaceShell';
import { generateAllTextures } from './textures';
import { LEVELS } from '../levels';
import { validateLevel } from '../levels/portHelpers';
import { fluidClassFor, moleculeMixEqual } from '../types';
import type { Cell } from '../types';
import { manhattanPath } from '../util/pathfinding';
import { GRID_SIZE, WORKSPACE } from '../grid';

const SAVE_KEY = 'gut-and-bolts.lastLevel';
// Set once the student beats the final level. Gates the level-select row so the
// student build hides it during normal play and reveals it permanently (even
// across reloads) after the game is beaten, for replaying specific levels.
const WON_KEY = 'gut-and-bolts.won';

function loadSavedLevel(): number {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (raw === null) return 0;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 0 || n >= LEVELS.length) return 0;
    return n;
  } catch {
    // localStorage may be unavailable (private mode, etc.)
    return 0;
  }
}

function saveLevel(index: number): void {
  try {
    localStorage.setItem(SAVE_KEY, String(index));
  } catch {
    // Silently ignore — progress just won't persist this session.
  }
}

function loadWonFlag(): boolean {
  try {
    return localStorage.getItem(WON_KEY) === '1';
  } catch {
    return false;
  }
}

function saveWonFlag(): void {
  try {
    localStorage.setItem(WON_KEY, '1');
  } catch {
    // Silently ignore — the level-select unlock just won't persist this session.
  }
}

/**
 * True when the page was opened with the `?unlock` query param — the teacher
 * URL (e.g. `https://gut-and-bolts.pages.dev/?unlock`) that reveals the
 * level-select row. The caller persists this via `saveWonFlag`, so visiting it
 * once on a device whose progress was lost (cache cleared) restores the row for
 * good — later visits to the plain URL stay unlocked.
 */
function unlockRequestedViaUrl(): boolean {
  try {
    return new URLSearchParams(window.location.search).has('unlock');
  } catch {
    return false;
  }
}

/**
 * Programmatic test surface for the scene. See `GameScene.debug` for the
 * runtime instance and the JSDoc comment there for usage.
 */
export interface GameSceneDebug {
  /** Construct a pipe spout → receptacle along the given cell path. */
  buildPipe: (
    spoutId: string,
    cells: { col: number; row: number }[],
    receptacleId: string,
  ) => string;
  /** Drop an organ from the current level's pool into the workspace at a cell. */
  placeOrgan: (defId: string, col: number, row: number) => string;
}

export class GameScene extends Phaser.Scene {
  private ports: Port[] = [];
  private pipes: Pipe[] = [];
  private organs: Organ[] = [];
  /**
   * Organs whose current drag was suppressed because the pointerdown actually
   * landed on a port cell — PipeBuilder is driving the gesture instead.
   * Cleared on the spurious dragend.
   */
  private organDragSuppressed = new Set<Organ>();
  private builder!: PipeBuilder;
  private connector!: Connector;
  private topBar!: TopBar;
  private clipboard!: Clipboard;
  private organPool!: OrganPool;
  private completionOverlay!: CompletionOverlay;
  private currentLevelIndex = 0;
  /** True once the student has beaten the final level (persisted in localStorage). */
  private hasWon = false;

  constructor() {
    super('GameScene');
  }

  preload(): void {
    this.load.audio('sfx-pipe-start', 'audio/pipe-start.mp3');
    this.load.audio('sfx-pipe-new-link', 'audio/pipe-new-link.mp3');
    this.load.audio('sfx-pipe-connect', 'audio/pipe-connect.mp3');
    this.load.audio('sfx-pipe-complete', 'audio/pipe-complete.mp3');
    this.load.audio('sfx-blood', 'audio/blood.mp3');
    this.load.audio('sfx-lung-diaphragm-drop', 'audio/lung-diaphragm-drop.mp3');
    this.load.audio('sfx-stomach-intestine-drop', 'audio/stomach-intestine-drop.mp3');
    this.load.audio('sfx-heart-drop', 'audio/heart-drop.mp3');
    this.load.audio('sfx-brain-drop', 'audio/brain-drop.mp3');
  }

  /**
   * Play a per-organ drop SFX iff the def matches a known organ. Mapping:
   *   lungs / diaphragm    → sfx-lung-diaphragm-drop
   *   stomach / intestines → sfx-stomach-intestine-drop
   *   heart                → sfx-heart-drop
   *   brain                → sfx-brain-drop
   * Returns true iff a sound was played (used by the group-drop site to
   * avoid double-firing when multiple matching organs are in the same group).
   */
  private playOrganDropSfx(def: import('../entities/Organ').OrganDef): boolean {
    if (def.art === 'lungs' || def.art === 'diaphragm') {
      this.sound.play('sfx-lung-diaphragm-drop');
      return true;
    }
    if (def.art === 'stomach' || def.art === 'intestines') {
      this.sound.play('sfx-stomach-intestine-drop');
      return true;
    }
    if (def.art === 'heart') {
      this.sound.play('sfx-heart-drop');
      return true;
    }
    if (def.art === 'brain') {
      this.sound.play('sfx-brain-drop');
      return true;
    }
    return false;
  }

  create(): void {
    generateAllTextures(this);
    drawWorkspaceShell(this);

    // Require the pointer to move 6px before Phaser fires `dragstart` on any
    // sprite. Without this, dragstart fires on pointer-press, which races the
    // scene-level `pointerdown` listener that PipeBuilder uses to claim the
    // press. The race lost on the heart bot cones (issue #1): the cone's
    // visual area overlaps the heart body sprite's bounds, so a press there
    // would trigger the organ's dragstart BEFORE PipeBuilder.handlePointerDown
    // ran — `isBuilding()` was still false, so the organ-drag-suppression
    // check in handleOrganDragStart didn't fire. With a 6px threshold,
    // pointerdown completes (PipeBuilder claims the port and sets
    // `currentPipe`), then the first pointermove past 6px triggers dragstart,
    // at which point `isBuilding()` is true and the organ drag is suppressed.
    this.input.dragDistanceThreshold = 6;

    // Build the level-select row entries: one button per step from 1 to
    // max(step). Steps with no matching LEVELS entry render as dimmed
    // placeholders (no click). Lets the row label the buttons by canonical
    // step number even when intermediate steps haven't shipped yet
    // (e.g. Levels 8–10 placeholders between L7 and L11).
    const maxStep = Math.max(...LEVELS.map((l) => l.step));
    const levelEntries = [];
    for (let step = 1; step <= maxStep; step++) {
      const levelIndex = LEVELS.findIndex((l) => l.step === step);
      levelEntries.push({ step, levelIndex: levelIndex >= 0 ? levelIndex : null });
    }

    this.topBar = new TopBar(this, {
      onClear: () => this.clearAllPipes(),
      onToggleFluids: () => this.toggleFluids(),
      onBack: () => this.goToLevel(this.currentLevelIndex - 1),
      onNext: () => this.goToLevel(this.currentLevelIndex + 1),
      onLevelSelect: (idx) => this.goToLevel(idx),
      levels: levelEntries,
    });

    // Level-select row is hidden for the student build and only unlocked after
    // the game is beaten. Restore that unlock if it was earned in a prior
    // session, or grant it via the `?unlock` teacher URL — persisted so it
    // survives later visits to the plain URL (heals cache-wiped devices).
    this.hasWon = loadWonFlag() || unlockRequestedViaUrl();
    if (this.hasWon) saveWonFlag();
    this.topBar.setLevelSelectVisible(this.hasWon);

    drawLegend(this);

    const startIndex = loadSavedLevel();
    const startLevel = LEVELS[startIndex];
    this.clipboard = new Clipboard(this, startLevel.title, startLevel.instructions);

    this.organPool = new OrganPool(this, {
      onPlace: (def, cell) => this.placeOrgan(def, cell),
      onDragStateChange: (dragging) => this.builder.setEnabled(!dragging),
    });

    this.builder = new PipeBuilder(
      this,
      () => this.ports,
      () => this.pipes,
      (pipe) => this.pipes.push(pipe),
      (pipe) => {
        this.pipes = this.pipes.filter((p) => p !== pipe);
        this.refreshDirectLinks();
      },
    );
    this.connector = new Connector(
      this,
      () => this.ports,
      () => this.pipes,
      () => this.organs,
      () => this.currentLevelActivationPortId(),
      () => this.stopBloodLoop(),
    );

    // Full-screen "You did it!" overlay shown when the final level is solved.
    // Replaying routes through goToLevel(0); goToLevel → unloadLevel already
    // hides the overlay + resets the one-shot flag, but we hide explicitly too.
    this.completionOverlay = new CompletionOverlay(this, () => {
      this.completionOverlay.hide();
      this.goToLevel(0);
    });

    this.loadLevel(startIndex);
  }

  /**
   * Programmatic test surface, exposed at runtime via
   * `window.__game.scene.getScene('GameScene').debug`. Used by manual
   * verification in the dev preview (Phaser doesn't have a unit-test framework
   * configured). Two helpers cover the common verification needs: build a pipe
   * along a known cell path, and drop an organ into the workspace.
   *
   * Backwards compatibility: also exposed as `debugBuildPipe` /
   * `debugPlaceOrgan` directly on the scene so older eval scripts keep working.
   */
  readonly debug: GameSceneDebug = {
    buildPipe: (spoutId, cells, receptacleId) => {
      const spout = this.ports.find((p) => p.id === spoutId);
      if (!spout) return 'no such spout';
      const pipe = new Pipe(this, spout.id, cells[0], spout.facing, fluidClassFor(spout.carries));
      for (let i = 1; i < cells.length; i++) pipe.tryExtendTo(cells[i]);
      pipe.connectTo(receptacleId);
      // Trachea detection (mirrors PipeBuilder.handlePointerUp): both endpoints
      // bidirectional → mark as a trachea pipe.
      const destPort = this.ports.find((p) => p.id === receptacleId);
      if (spout.kind === 'bidirectional' && destPort?.kind === 'bidirectional') {
        pipe.specialKind = 'trachea';
      }
      this.pipes.push(pipe);
      return 'ok';
    },
    placeOrgan: (defId, col, row) => {
      const cfg = LEVELS[this.currentLevelIndex];
      const def = cfg.organPool?.find((o) => o.id === defId);
      if (!def) return 'no such organ in pool';
      const ok = this.placeOrgan(def, { col, row });
      if (ok) this.organPool.removeIcon(defId);
      return ok ? 'placed' : 'failed';
    },
  };

  /** Legacy aliases — older preview-eval scripts call these directly on the scene. */
  debugBuildPipe = (
    spoutId: string,
    cells: { col: number; row: number }[],
    receptacleId: string,
  ): string => this.debug.buildPipe(spoutId, cells, receptacleId);
  debugPlaceOrgan = (defId: string, col: number, row: number): string =>
    this.debug.placeOrgan(defId, col, row);

  // ---------------- Level management ----------------

  private loadLevel(index: number): void {
    this.currentLevelIndex = index;
    const cfg = LEVELS[index];
    // Sanity check at load time — warns about duplicate ports, UI keep-out
    // violations, etc. Output goes to console.warn only; the level still loads.
    for (const w of validateLevel(cfg)) console.warn(w);
    this.clipboard.setText(cfg.title, cfg.instructions);
    for (const portCfg of cfg.ports) {
      this.ports.push(new Port(this, portCfg));
    }
    this.organPool.setOrgans(cfg.organPool ?? []);
    this.topBar.setBackEnabled(index > 0);
    this.topBar.setNextVisible(false);
    this.topBar.setCurrentLevelHighlight(index);
  }

  private unloadLevel(): void {
    this.nextRevealed = false;
    this.completionShown = false;
    this.completionOverlay.hide();
    for (const port of this.ports) port.container.destroy();
    this.ports = [];
    for (const pipe of this.pipes) pipe.destroyPipe();
    this.pipes = [];
    for (const organ of this.organs) organ.destroy();
    this.organs = [];
    this.organPool.clear();
    this.connector.clearAll();
    this.stopBloodLoop();
    this.topBar.setFluidsState('idle');
    this.builder.setEnabled(true);
  }

  private goToLevel(index: number): void {
    if (index < 0 || index >= LEVELS.length) return;
    this.unloadLevel();
    this.loadLevel(index);
    saveLevel(index);
  }

  private currentLevelActivationPortId(): string | undefined {
    return LEVELS[this.currentLevelIndex].activationPortId;
  }

  // ---------------- Organ placement ----------------

  private placeOrgan(
    def: import('../entities/Organ').OrganDef,
    cell: { col: number; row: number },
  ): boolean {
    // Two organs may overlap by exactly one cell-thick strip if BOTH have castellated
    // ports — that strip is the row/col where their joint forms.
    for (const o of this.organs) {
      if (!this.rectsOverlap(cell, def.cols, def.rows, o.cell, o.def.cols, o.def.rows)) continue;
      const isThinJointStrip = this.overlapIsOneCellStrip(
        cell,
        def.cols,
        def.rows,
        o.cell,
        o.def.cols,
        o.def.rows,
      );
      const bothCastellated =
        def.ports.some((p) => p.shape === 'castellated') &&
        o.def.ports.some((p) => p.shape === 'castellated');
      if (!(isThinJointStrip && bothCastellated)) return false;
    }
    const organ = new Organ(this, def, cell);
    this.organs.push(organ);
    this.ports.push(...organ.ports);
    this.attachOrganDragHandlers(organ);
    this.refreshDirectLinks();
    this.playOrganDropSfx(def);
    return true;
  }

  /** Returns true if two rectangles' overlap is exactly one cell thick on one axis. */
  private overlapIsOneCellStrip(
    a: { col: number; row: number },
    aw: number,
    ah: number,
    b: { col: number; row: number },
    bw: number,
    bh: number,
  ): boolean {
    const overlapW = Math.max(0, Math.min(a.col + aw, b.col + bw) - Math.max(a.col, b.col));
    const overlapH = Math.max(0, Math.min(a.row + ah, b.row + bh) - Math.max(a.row, b.row));
    if (overlapW === 0 || overlapH === 0) return false;
    return overlapW === 1 || overlapH === 1;
  }

  private attachOrganDragHandlers(organ: Organ): void {
    organ.sprite.on('dragstart', () => this.handleOrganDragStart(organ));
    organ.sprite.on('drag', (_pointer: Phaser.Input.Pointer, dragX: number, dragY: number) => {
      this.handleOrganDrag(organ, dragX, dragY);
    });
    organ.sprite.on('dragend', () => this.handleOrganDragEnd(organ));
  }

  /** All organs that are directly-linked (transitively) to `seed`, including itself. */
  private getOrganGroup(seed: Organ): Organ[] {
    const group = new Set<Organ>([seed]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const pipe of this.pipes) {
        if (!pipe.directLink) continue;
        const ownerOfPort = (portId: string) =>
          this.organs.find((o) => o.ports.some((p) => p.id === portId));
        const a = ownerOfPort(pipe.fromPortId);
        const b = pipe.toPortId ? ownerOfPort(pipe.toPortId) : undefined;
        if (a && b && group.has(a) !== group.has(b)) {
          group.add(a);
          group.add(b);
          changed = true;
        }
      }
    }
    return [...group];
  }

  private handleOrganDragStart(organ: Organ): void {
    // If PipeBuilder won the pointerdown (pointer landed on a port-cell that
    // happens to be inside this organ's body — e.g. heart bot outputs at the
    // bottom row of the body), let it drive: the student wanted to drag a
    // pipe, not move the organ. Mark this drag as suppressed and bail.
    if (this.builder.isBuilding()) {
      this.organDragSuppressed.add(organ);
      return;
    }
    this.builder.cancelCurrent();
    this.builder.setEnabled(false);
    const group = this.getOrganGroup(organ);
    // Dim every organ in the group so the student sees them as a single moving unit.
    for (const o of group) o.sprite.setAlpha(0.7);
  }

  private handleOrganDrag(organ: Organ, dragX: number, dragY: number): void {
    if (this.organDragSuppressed.has(organ)) return;
    const dx = dragX - organ.sprite.x;
    const dy = dragY - organ.sprite.y;
    const group = this.getOrganGroup(organ);
    for (const o of group) {
      o.sprite.setPosition(o.sprite.x + dx, o.sprite.y + dy);
      // Move the visible port containers along with the sprite so the organ "feels" like one unit.
      for (const p of o.ports) {
        p.container.setPosition(p.container.x + dx, p.container.y + dy);
      }
    }
  }

  private handleOrganDragEnd(organ: Organ): void {
    if (this.organDragSuppressed.has(organ)) {
      this.organDragSuppressed.delete(organ);
      return;
    }
    const group = this.getOrganGroup(organ);
    for (const o of group) o.sprite.setAlpha(1);

    // Snap delta (in grid cells) from the primary organ's current sprite position.
    const targetPrimary = this.spriteToTopLeftCell(organ);
    const dCol = targetPrimary.col - organ.cell.col;
    const dRow = targetPrimary.row - organ.cell.row;

    // Proposed new cells for every group member (preserve relative positions).
    const newCells = new Map<Organ, Cell>();
    for (const o of group) newCells.set(o, { col: o.cell.col + dCol, row: o.cell.row + dRow });

    const accepted = this.validateGroupPlacement(group, newCells);
    this.commitOrganPlacement(group, accepted ? newCells : null);

    if (accepted) {
      const movedPortIds = new Set(group.flatMap((o) => o.ports.map((p) => p.id)));
      this.rerouteAffectedPipes(movedPortIds);
      // Play organ-drop SFX for any matching organ in the moved group (covers
      // both standalone moves and group-drags of direct-linked organs).
      // Each SFX plays at most once even if multiple matching organs are in
      // the group — the SFX is a single "thunk" not meant to stack. If two
      // different pairs are in the same group (hypothetical), both sounds
      // play simultaneously.
      const arts = new Set(group.map((o) => o.def.art));
      if (arts.has('lungs') || arts.has('diaphragm')) {
        this.sound.play('sfx-lung-diaphragm-drop');
      }
      if (arts.has('stomach') || arts.has('intestines')) {
        this.sound.play('sfx-stomach-intestine-drop');
      }
      if (arts.has('heart')) {
        this.sound.play('sfx-heart-drop');
      }
      if (arts.has('brain')) {
        this.sound.play('sfx-brain-drop');
      }
    }

    this.refreshDirectLinks();
    this.builder.setEnabled(true);
  }

  /** True iff every organ in the group fits in-bounds and only overlaps non-group
   *  organs at a 1-cell castellated joint strip. */
  private validateGroupPlacement(group: Organ[], newCells: Map<Organ, Cell>): boolean {
    return [...newCells.entries()].every(([o, c]) => {
      const inBounds =
        c.col >= 0 &&
        c.row >= 0 &&
        c.col + o.def.cols <= WORKSPACE.cols &&
        c.row + o.def.rows <= WORKSPACE.rows;
      if (!inBounds) return false;
      const overlapsOther = this.organs
        .filter((other) => !group.includes(other))
        .some((other) => {
          if (
            !this.rectsOverlap(
              c,
              o.def.cols,
              o.def.rows,
              other.cell,
              other.def.cols,
              other.def.rows,
            )
          )
            return false;
          const isThinJointStrip = this.overlapIsOneCellStrip(
            c,
            o.def.cols,
            o.def.rows,
            other.cell,
            other.def.cols,
            other.def.rows,
          );
          const bothCastellated =
            o.def.ports.some((p) => p.shape === 'castellated') &&
            other.def.ports.some((p) => p.shape === 'castellated');
          return !(isThinJointStrip && bothCastellated);
        });
      return !overlapsOther;
    });
  }

  /** Snap each organ's sprite to its target cell (new if accepted, original if not)
   *  and rebuild its port instances. */
  private commitOrganPlacement(group: Organ[], newCells: Map<Organ, Cell> | null): void {
    for (const o of group) {
      const target = newCells ? newCells.get(o)! : o.cell;
      const oldPortIds = new Set(o.ports.map((p) => p.id));
      this.ports = this.ports.filter((p) => !oldPortIds.has(p.id));
      o.snapSpriteTo(target);
      o.rebuildPortsAt(target);
      this.ports.push(...o.ports);
    }
  }

  /** For every non-direct pipe that touches a moved port, re-route via Manhattan L-path. */
  private rerouteAffectedPipes(movedPortIds: Set<string>): void {
    for (const pipe of this.pipes) {
      if (pipe.directLink) continue;
      const touchesMoved =
        movedPortIds.has(pipe.fromPortId) ||
        (pipe.toPortId !== null && movedPortIds.has(pipe.toPortId));
      if (!touchesMoved) continue;
      const spout = this.ports.find((p) => p.id === pipe.fromPortId);
      const receptacle = pipe.toPortId ? this.ports.find((p) => p.id === pipe.toPortId) : null;
      if (!spout || !receptacle) continue;
      const segments = manhattanPath(spout.connectionCell, receptacle.connectionCell, spout.facing);
      pipe.rebuildSegments(segments);
    }
  }

  /** Convert the organ sprite's current world position to its snapped top-left cell. */
  private spriteToTopLeftCell(organ: Organ): Cell {
    const halfW = (organ.def.cols * GRID_SIZE) / 2;
    const halfH = (organ.def.rows * GRID_SIZE) / 2;
    return {
      col: Math.round((organ.sprite.x - halfW - WORKSPACE.originX) / GRID_SIZE),
      row: Math.round((organ.sprite.y - halfH - WORKSPACE.originY) / GRID_SIZE),
    };
  }

  private rectsOverlap(
    a: { col: number; row: number },
    aw: number,
    ah: number,
    b: { col: number; row: number },
    bw: number,
    bh: number,
  ): boolean {
    return !(
      a.col + aw <= b.col ||
      b.col + bw <= a.col ||
      a.row + ah <= b.row ||
      b.row + bh <= a.row
    );
  }

  /**
   * Scan organ ports for shared connection cells. Each matching pair (spout +
   * receptacle, identical `carries`) becomes a "direct link" — a Pipe with a
   * single segment at the shared cell, plus a permanent yellow highlight on
   * both cones. Existing direct links whose endpoints no longer align are
   * torn down. Highlights are reset and re-applied each call so the visual
   * state always matches the current organ layout.
   */
  private refreshDirectLinks(): void {
    const organPorts = this.organs.flatMap((o) => o.ports);
    const validPairs = this.findCastellatedPairs(organPorts);
    this.tearDownStaleDirectLinks(validPairs);
    for (const p of organPorts) p.setDirectLinkHighlight(false);
    for (const pair of validPairs) this.ensureDirectLinkPipe(pair);
  }

  private findCastellatedPairs(organPorts: Port[]): Array<{ spout: Port; receptacle: Port }> {
    const pairs: Array<{ spout: Port; receptacle: Port }> = [];
    const spouts = organPorts.filter((p) => p.kind === 'spout');
    const receptacles = organPorts.filter((p) => p.kind === 'receptacle');
    for (const sp of spouts) {
      for (const rc of receptacles) {
        if (
          sp.connectionCell.col === rc.connectionCell.col &&
          sp.connectionCell.row === rc.connectionCell.row &&
          moleculeMixEqual(sp.carries, rc.carries)
        ) {
          pairs.push({ spout: sp, receptacle: rc });
        }
      }
    }
    return pairs;
  }

  private tearDownStaleDirectLinks(validPairs: Array<{ spout: Port; receptacle: Port }>): void {
    const validKey = (a: string, b: string) => `${a}->${b}`;
    const validSet = new Set(validPairs.map((p) => validKey(p.spout.id, p.receptacle.id)));
    for (const pipe of [...this.pipes]) {
      if (!pipe.directLink) continue;
      const key = pipe.toPortId ? validKey(pipe.fromPortId, pipe.toPortId) : '';
      if (!validSet.has(key)) {
        pipe.destroyPipe();
        this.pipes = this.pipes.filter((p) => p !== pipe);
      }
    }
  }

  private ensureDirectLinkPipe({ spout, receptacle }: { spout: Port; receptacle: Port }): void {
    const exists = this.pipes.some(
      (p) => p.directLink && p.fromPortId === spout.id && p.toPortId === receptacle.id,
    );
    if (!exists) {
      const pipe = new Pipe(
        this,
        spout.id,
        spout.connectionCell,
        spout.facing,
        fluidClassFor(spout.carries),
      );
      pipe.connectTo(receptacle.id);
      pipe.markAsDirectLink();
      this.pipes.push(pipe);
    }
    spout.setDirectLinkHighlight(true);
    receptacle.setDirectLinkHighlight(true);
  }

  // ---------------- Input handlers ----------------

  private clearAllPipes(): void {
    this.connector.clearAll();
    this.stopBloodLoop();
    for (const pipe of this.pipes) pipe.destroyPipe();
    this.pipes = [];
    // CLEAR destroys ALL pipes, including the invisible organ-to-organ direct
    // links (e.g. the lungs↔diaphragm pulse). Rebuild them from the current
    // organ layout so a placed castellated pair stays linked. Without this the
    // joint keeps its yellow highlight but silently loses its pulse pipe, and
    // the level becomes unsolvable until an organ is nudged (the only other
    // caller of refreshDirectLinks). This also re-syncs the direct-link
    // highlights so they always match the actual link state.
    this.refreshDirectLinks();
    this.builder.clearDeleteMark();
    this.nextRevealed = false;
    this.completionShown = false;
    this.completionOverlay.hide();
    // Placed organs stay where they are — only pipes get cleared.
    this.topBar.setFluidsState('idle');
    this.topBar.setNextVisible(false);
    this.builder.setEnabled(true);
  }

  /** Looping "blood" SFX played while fluids are running cleanly. */
  private bloodLoop: Phaser.Sound.BaseSound | null = null;

  private startBloodLoop(): void {
    if (this.bloodLoop) return;
    this.bloodLoop = this.sound.add('sfx-blood', { loop: true });
    this.bloodLoop.play();
  }

  private stopBloodLoop(): void {
    if (!this.bloodLoop) return;
    this.bloodLoop.stop();
    this.bloodLoop.destroy();
    this.bloodLoop = null;
  }

  private toggleFluids(): void {
    if (this.topBar.getFluidsState() === 'idle') {
      // Guard: re-sync organ-to-organ direct links to the current organ layout
      // before we classify flow. The solved check depends on direct-link pipes
      // (e.g. the lungs' diaphragm pulse) being present and flowing; rebuilding
      // here guarantees they match the actual castellated-pair layout no matter
      // how pipes were last mutated, so a stale or missing link can never block
      // a correct solve. refreshDirectLinks is idempotent — it only creates
      // missing links, tears down stale ones, and re-applies highlights.
      this.refreshDirectLinks();
      // Start the loop BEFORE startFluids so any t=0 spurts/bursts emitted
      // synchronously during startFluids stop the loop immediately via the
      // onSpurtOrBurst callback wired into Connector.
      this.startBloodLoop();
      this.connector.startFluids();
      this.topBar.setFluidsState('flowing');
      this.builder.setEnabled(false);
      this.nextRevealed = false;
      this.completionShown = false;
      // The per-frame `update()` loop polls `Connector.isLevelSolved` while
      // fluids are running and reveals NEXT the first time it returns true.
      // No deferred timer — the previous one-shot at `maxFlowTravelMs + 250`
      // misfired on cascades involving wall-port `dependsOn` (the DAG walk
      // doesn't account for upstream unconditional travel time, so the
      // estimate fires before the cascade settles). Per-frame is correct
      // regardless of cascade shape.
    } else {
      this.connector.stopFluids();
      this.stopBloodLoop();
      this.topBar.setFluidsState('idle');
      this.topBar.setNextVisible(false);
      this.nextRevealed = false;
      this.completionShown = false;
      this.completionOverlay.hide();
      this.builder.setEnabled(true);
    }
  }

  /** True once `setNextVisible(true)` fired in the current fluid run. */
  private nextRevealed = false;

  /**
   * True once the completion overlay was shown for the current fluid run
   * (final level only). Mirrors `nextRevealed` — reset wherever that is, so the
   * overlay appears at most once per solve and re-arms on STOP/CLEAR/level
   * change.
   */
  private completionShown = false;

  private countSpouts(): number {
    return this.ports.filter((p) => p.kind === 'spout').length;
  }

  /**
   * Structural solve prerequisites from `LevelConfig.requiredLinks` — every
   * listed port pair must be joined by a flowing pipe (either drag
   * orientation). ANDed into the solved check so the correct-spout COUNT alone
   * can't complete a level that skips a mandatory connection (e.g. the L5/L8
   * trachea, which a student could otherwise bypass by piping the
   * bidirectional atmosphere straight into the O₂ wall receptacle).
   */
  private requiredLinksSatisfied(): boolean {
    const links = LEVELS[this.currentLevelIndex].requiredLinks;
    if (!links || links.length === 0) return true;
    return links.every((lk) =>
      this.pipes.some(
        (p) =>
          p.state === 'flowing' &&
          ((p.fromPortId === lk.a && p.toPortId === lk.b) ||
            (p.fromPortId === lk.b && p.toPortId === lk.a)),
      ),
    );
  }

  /**
   * Phaser per-frame hook. Drives the v5 per-spout rule eval — see
   * `Connector.update` for details — and polls `Connector.isLevelSolved`
   * to reveal NEXT once the cascade settles. Polling instead of a one-shot
   * timer keeps the reveal robust against arbitrary cascade depth
   * (`maxFlowTravelMs` underestimates wall-port `dependsOn` chains).
   * No-op when fluids aren't running.
   */
  update(time: number, delta: number): void {
    this.connector.update(time, delta);

    const isLastLevel = this.currentLevelIndex === LEVELS.length - 1;
    const fluidsFlowing = this.topBar.getFluidsState() === 'flowing';

    // On the final level, a solve shows the celebratory completion overlay
    // instead of revealing NEXT (there's no next level). Both paths are guarded
    // by independent one-shot flags so each fires at most once per fluid run.
    if (
      isLastLevel &&
      !this.completionShown &&
      fluidsFlowing &&
      this.connector.isLevelSolved(this.countSpouts()) &&
      this.requiredLinksSatisfied()
    ) {
      this.completionShown = true;
      // Beating the final level permanently unlocks the level-select row so the
      // student can replay any level. Persist it so it survives reloads.
      if (!this.hasWon) {
        this.hasWon = true;
        saveWonFlag();
        this.topBar.setLevelSelectVisible(true);
      }
      this.completionOverlay.show();
      this.stopBloodLoop();
    } else if (
      !isLastLevel &&
      !this.nextRevealed &&
      fluidsFlowing &&
      this.connector.isLevelSolved(this.countSpouts()) &&
      this.requiredLinksSatisfied()
    ) {
      this.nextRevealed = true;
      this.topBar.setNextVisible(true);
      this.stopBloodLoop();
    }
  }
}
